import { IDefinedAdUnits } from '../ad-services/DFP/service-dfp.types';
import { IAd, IConfig, IDetection, IPrebidsConfig, ISlot, SizeType } from '../../uanc.types';
import {
    adaptersMap,
    JUSTTAG_OFF,
    modulesMap,
    NATIVE_SERVICES,
    NATIVE_SGP,
    NATIVE_SLOTS,
    NATIVE_SLOTS_POCZTA,
    nativeObjectPOCZTA,
    PREBID_DSA,
    prebidAdapterNames,
    prebidModuleNames,
} from './config';
import DebugUANC from '../../core/version/debug';
import { IAdConsolePassPrebidData, IBidResponses, IPbjs, IPbjsAdUnit, IPbjsBid, IPbjsBidResponse, IPbjsConfig } from './prebid.types';
import Slot = googletag.Slot;
import {
    cleanBidInfo,
    createNativePrebidAdUnit,
    createPrebidAdUnit,
    createSGPNativePrebidAdUnit,
    getSizes,
    getSizesIX,
    getSlotBidderNames,
    getSlotConfigWithBidders,
    isPrebidAccepted,
    prepareBids,
    updateBidInfo,
} from './prebid.utils';
import { PLUGIN_NAME } from '../plugins/config';
import { getWindowContext } from '../../core/pre-config/pre-config.utils';
import { loadPREBIDScript } from '../../helpers/load-prebid-script';

/**
 * Ładowanie modułów odpowiedzialnych za obsługę konkretnych bidderów.
 *
 * @param { string[] } bidderNames - nazwy bidderów do załadowania
 * @returns {Promise<void>}
 */
export const importAdapters = async (bidderNames: string[]): Promise<void> => {
    const { UANC } = window.Inpl.Ad;
    await Promise.all(
        bidderNames.map((bidderName) => {
            if (prebidAdapterNames.includes(bidderName) && bidderName in adaptersMap) {
                return adaptersMap[bidderName]().catch((error) => {
                    UANC instanceof DebugUANC &&
                        UANC.warn(`[Prebid importAdapters] Error while importing adapter for bidder: ${bidderName}. ${error}`);
                    return Promise.resolve();
                });
            } else {
                UANC instanceof DebugUANC && UANC.warn(`[Prebid importAdapters] No adapter for loading: ${bidderName}.`);
                return Promise.resolve();
            }
        })
    );
};

/**
 * Obsługa prebid
 *
 * @param { IDefinedAdUnits} definedAdUnits - sloty z nazwami zdefiniowanych placementów / jednostek reklamowych
 * @returns {Promise<void>}
 */
export const requestForBids = async (definedAdUnits: IDefinedAdUnits): Promise<void> => {
    const { UANC, config, settings, plugins, exSettings, detection } = window.Inpl.Ad;

    const startTime: number = Date.now();
    const startTimeDate = new Date(startTime).toTimeString().split(' ')[0]; // "HH:MM:SS";

    const prebidsConfig = config?.prebidsConfig as IPrebidsConfig;
    const isIncludeNativeSGP = '44520695/17interia/sgp2021' === config?.adPath;
    const slotsConfigWithBidders: ISlot[] = getSlotConfigWithBidders(Object.keys(definedAdUnits), isIncludeNativeSGP);

    if (!isPrebidAccepted() || !slotsConfigWithBidders.length) return;
    const pbjs = getWindowContext().pbjs;
    if (!pbjs?.que) {
        UANC instanceof DebugUANC &&
            UANC.warn('[requestForBids] Bid request canceled - operation cannot be performed - Prebid has not been initiated.');
        return;
    }

    const adapterNamesToImport: string[] = getSlotBidderNames(slotsConfigWithBidders, isIncludeNativeSGP);
    !exSettings?.isPoczta && (await importAdapters(adapterNamesToImport));

    let adConsolePassPrebidData: IAdConsolePassPrebidData | null = null;
    if (plugins && PLUGIN_NAME.AD_CONSOLE in plugins && plugins[PLUGIN_NAME.AD_CONSOLE]?.isEnabled) {
        try {
            // AdConsole - metody przekazujące informacje o prebid (request, response, bidWin)
            await import('../plugins/AdConsole/AdConsoleMain/templateAdConsoleMain').then((module) => {
                adConsolePassPrebidData = {
                    showPrebidWinBidderData: module.showPrebidWinBidderData,
                    showPrebidRequestData: module.showPrebidRequestData,
                    showPrebidResponseData: module.showPrebidResponseData,
                } as IAdConsolePassPrebidData;
            });
        } catch (e) {
            UANC instanceof DebugUANC && UANC.warn('[requestForBids] Problem importing AdConsole modules to pass data.');
        }
    }

    pbjs.onEvent('bidWon', (data) => {
        UANC instanceof DebugUANC &&
            UANC.success(`WINNER BIDDER: ${data.bidderCode} won the ad server auction for ad unit ${data.adUnitCode} at ${data.cpm} CPM`);

        if (adConsolePassPrebidData?.showPrebidWinBidderData) {
            const placementName = data.adUnitCode;
            const slotName = Object.keys(definedAdUnits).find((adUnits) => definedAdUnits[adUnits].includes(placementName));
            adConsolePassPrebidData.showPrebidWinBidderData(slotName ?? '---', placementName, data);
        }
    });

    const passInfoToAdConsole = (infoType: 'REQUEST' | 'RESPONSE', data: IPbjsBid[] | IBidResponses, slotName: string | null = null) => {
        if (infoType === 'REQUEST') {
            const bids = data as IPbjsBid[];
            Object.keys(definedAdUnits).forEach((slotNameAdUnit) => {
                if (slotNameAdUnit === slotName) {
                    // przekazanie informacje o ustawieniach bids dla każdego placementu slotu
                    definedAdUnits[slotNameAdUnit].forEach((placement) => {
                        adConsolePassPrebidData?.showPrebidRequestData(slotNameAdUnit, placement, bids);
                    });
                }
            });
        }

        if (infoType === 'RESPONSE') {
            const bidResponses = data as IBidResponses;
            Object.entries(bidResponses).forEach(([placement, { bids }]) => {
                const slotName = Object.keys(definedAdUnits).find((adUnits) => definedAdUnits[adUnits].includes(placement));
                const dataToPass: IPbjsBidResponse[] = bids.map((bid) => ({
                    ...bid,
                    ad: bid.ad ? bid.ad.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '',
                }));
                adConsolePassPrebidData?.showPrebidResponseData(slotName ?? '---', placement, dataToPass);
            });
        }
    };

    return new Promise((resolve) => {
        try {
            const adUnits: IPbjsAdUnit[] = [];
            slotsConfigWithBidders.forEach((slot) => {
                let bids: IPbjsBid[] = prepareBids(slot.bidders!);
                if (settings?.serviceId === 1050 || settings?.serviceId === 1035) {
                    if (slot.name === 'rectangle_news' || slot.name === 'rectangle') {
                        bids = bids.filter((item) => item.bidder !== 'teads');
                    }
                }

                if (adConsolePassPrebidData?.showPrebidRequestData) {
                    passInfoToAdConsole('REQUEST', bids, slot.name);
                }

                const sizes: SizeType[] = getSizes(slot.mapping || []);
                const sizesIX: SizeType[] = adapterNamesToImport.includes('ix') ? getSizesIX(sizes) : [];

                //native sloty dla SGP
                if (isIncludeNativeSGP && slot.name in NATIVE_SGP) {
                    const nativeAdUnits: IPbjsAdUnit[] = createSGPNativePrebidAdUnit(slot.name, definedAdUnits[slot.name]);
                    adUnits.push(...nativeAdUnits);
                }

                //native sloty dla Poczty
                if (exSettings?.isPoczta && NATIVE_SLOTS_POCZTA.includes(slot.name)) {
                    const nativeAdUnits: IPbjsAdUnit[] = definedAdUnits[slot.name].map((adUnitName) => ({
                        code: adUnitName,
                        mediaTypes: {
                            native: nativeObjectPOCZTA(detection as IDetection),
                        },
                        bids,
                    }));

                    adUnits.push(...nativeAdUnits);
                }

                // native sloty dla konkretnych adPath (native services)
                if (NATIVE_SLOTS.includes(slot.name) && NATIVE_SERVICES.includes(config!.adPath) && sizes.length > 0) {
                    const nativeAdUnits: IPbjsAdUnit[] = createNativePrebidAdUnit(
                        slot.name,
                        definedAdUnits[slot.name],
                        bids,
                        sizes,
                        sizesIX
                    );
                    adUnits.push(...nativeAdUnits);
                } else if ((sizes.length !== 0 || sizesIX.length) && !NATIVE_SGP[slot.name]) {
                    const pbjsAdUnits: IPbjsAdUnit[] = createPrebidAdUnit(definedAdUnits[slot.name], bids, sizes, sizesIX);
                    adUnits.push(...pbjsAdUnits);
                }
            });

            if (!adUnits.length) return resolve();
            if (UANC instanceof DebugUANC) {
                UANC.info(`[Prebid - new request] startTime:  ${startTimeDate}, ${startTime}`);
                console.log('[Prebid - new request] adUnits: ', adUnits);
            }

            pbjs.que.push(function () {
                // żądanie licytacji
                pbjs.requestBids({
                    adUnits,
                    bidsBackHandler: (bidResponses, timedOut) => {
                        if (UANC instanceof DebugUANC) {
                            UANC.success('[Prebid - response]');
                            console.log(
                                '[Prebid - response] - request started at:',
                                startTimeDate,
                                startTime,
                                'TimeToRespond: ',
                                Date.now() - startTime,
                                'bidResponses:',
                                bidResponses,
                                'isTimeout: ',
                                timedOut
                            );
                        }
                        if (timedOut || !bidResponses) {
                            cleanBidInfo(adUnits, slotsConfigWithBidders);
                            return resolve();
                        }

                        pbjs.setTargetingForGPTAsync(Object.keys(bidResponses));
                        updateBidInfo(bidResponses, slotsConfigWithBidders);

                        if (adConsolePassPrebidData?.showPrebidResponseData) {
                            passInfoToAdConsole('RESPONSE', bidResponses);
                        }

                        if (exSettings?.isPoczta) {
                            for (const { bids } of Object.values(bidResponses)) {
                                for (const bid of bids) {
                                    // dodanie informacji o nazwie adUnit/placementId - niezbędne w renderer.js
                                    if (bid.mediaType === 'native' && bid.native) {
                                        bid.native.ext = bid.native.ext ?? {};
                                        bid.native.ext.adUnitCode = bid.adUnitCode;
                                    }
                                }
                            }
                        }

                        return resolve();
                    },
                    timeout: settings && settings.serviceId === 1021 ? 2000 : prebidsConfig.timeout,
                });
            });
        } catch (error) {
            UANC instanceof DebugUANC && UANC.warn(`[requestForBids] Problem with pbjs ad units & requestBids. ${error}`);
            return resolve();
        }
    });
};

/**
 * Ładowanie modułów związanych z obsługą i działaniem prebid.
 *
 * @returns {Promise<void>}
 */
export const importModules = async (): Promise<void> => {
    const { UANC } = window.Inpl.Ad;
    await Promise.all(
        prebidModuleNames.map((moduleName) => {
            if (prebidModuleNames.includes(moduleName) && moduleName in modulesMap) {
                return modulesMap[moduleName]().catch((error) => {
                    UANC instanceof DebugUANC && UANC.warn(`[Prebid importModules] Error while importing module: ${moduleName}. ${error}`);
                    return Promise.resolve();
                });
            } else {
                UANC instanceof DebugUANC && UANC.warn(`[Prebid importModules] No module for loading: ${moduleName}.`);
                return Promise.resolve();
            }
        })
    );
};

/**
 * Zwraca konfigurację prebida.
 *
 * @returns {IPbjsConfig} konfiguracja prebid
 */
const getPrebidConfig = (): IPbjsConfig => {
    const { config, detection, tcfData } = window.Inpl.Ad as IAd;
    const { prebidsConfig, adPath } = config as IConfig;

    const tcfParams: number[] = [];
    let required: number = 2;
    if (tcfData) {
        const TCFPurposes4 = tcfData.purpose.consents[4];
        const TCFPurposes2 = tcfData.purpose.consents[2];
        const TCFSpecialFeature = tcfData.specialFeatureOptins[1];
        if (TCFPurposes4) tcfParams.push(1);
        if (TCFPurposes2) tcfParams.push(2);
        if (TCFSpecialFeature) tcfParams.push(3);

        if (PREBID_DSA.find((item) => item === adPath)) {
            required = 3;
        }
    }

    const prebidConfigToSet: IPbjsConfig = {
        bidderSequence: prebidsConfig.enabledOrder ? 'fixed' : 'random',
        maxRequestsPerOrigin: 4,
        priceGranularity: prebidsConfig.priceGranularity || 'dense',
        currency: {
            adServerCurrency: prebidsConfig.currency,
            defaultRates: { USD: { PLN: 3.85 } },
        },
        enableTIDs: true,
        consentManagement: {
            gdpr: {
                cmpApi: 'iab',
                timeout: 2000,
                allowAuctionWithoutConsent: false,
            },
        },
        fledgeForGpt: {
            enabled: true,
            bidders: ['rtbhouse'],
            defaultForSlots: 1,
        },
        ortb2: {
            regs: {
                ext: {
                    dsa: {
                        dsarequired: required,
                        pubrender: 0,
                        datatopub: 2,
                        transparency: [
                            {
                                domain: detection.hostName,
                                dsaparams: tcfParams,
                            },
                        ],
                    },
                },
            },
        },
        userSync: {
            filterSettings: {
                iframe: {
                    bidders: '*',
                    filter: 'include',
                },
            },
            userIds: [
                {
                    name: 'justId',
                    params: {
                        mode: 'COMBINED',
                        url: 'https://id.nsaudience.pl/getId.js',
                    },
                    storage: {
                        expires: 365,
                        name: 'justId',
                        refreshInSeconds: 1,
                        type: 'html5',
                    },
                },
                {
                    name: 'teadsId',
                    params: {
                        pubId: 28047,
                    },
                },
            ],
        },
        gptPreAuction: {
            enabled: true,
            customPreAuction: (adUnit, adServerAdSlot) => {
                const regex = /.*_\d\d$/;

                if (adServerAdSlot) {
                    //dodajemy # jesli to slot powielany
                    return adServerAdSlot + `${regex.test(adUnit.code.slice(-3)) ? '#' + adUnit.code : ''}`;
                }

                const gptSlots = getWindowContext()
                    .googletag.pubads()
                    .getSlots()
                    .filter(function (gpt: Slot) {
                        if (gpt.getSlotElementId() === adUnit.code) return gpt;
                    });
                return gptSlots[0].getAdUnitPath() + `${regex.test(adUnit.code.slice(-3)) ? '#' + adUnit.code : ''}`;
            },
        },
    };

    if (JUSTTAG_OFF.find((item) => item === location.hostname)) {
        prebidConfigToSet.userSync!.userIds = [
            {
                name: 'teadsId',
                params: {
                    pubId: 15614,
                },
            },
        ];
    }

    prebidsConfig.buckets?.length &&
        Object.assign(prebidConfigToSet, {
            priceGranularity: { buckets: prebidsConfig.buckets },
        });

    return prebidConfigToSet;
};

/**
 * Załadowanie prebid oraz modułów. Ustawienie konfiguracji.
 * @param {Window} windowToLoadScript - opcjonalnie, przekazany obiekt window, np.: w kontekście iframe - iframe.contentWindow na potrzeby osadzenia skryptu wewnątrz konkretnego iframe
 *
 * @returns {void}
 */
export const initPrebid = async (windowToLoadScript: Window = window): Promise<void> => {
    const { UANC, exSettings, detection } = window.Inpl.Ad;
    return new Promise((resolve) => {
        (async () => {
            try {
                // poczta - prebid ładowany przez inline script do konkretnego iframe
                if (exSettings?.isPoczta) await loadPREBIDScript(windowToLoadScript);
                else {
                    windowToLoadScript.pbjs = (
                        (await import(/* webpackChunkName: "prebid" */ 'prebid.js')) as unknown as { default: IPbjs }
                    ).default;
                }

                if (typeof windowToLoadScript.pbjs?.processQueue === 'function' && windowToLoadScript.pbjs?.que) {
                    windowToLoadScript.pbjs.processQueue();
                } else {
                    throw new Error('Pbjs does not exist.');
                }
                windowToLoadScript.pbjs.que.push(async () => {
                    UANC?.setState({ isPrebidReady: true });
                    UANC instanceof DebugUANC && UANC.success('[initPrebid] Prebid is ready for work.');

                    // poczta - cały prebid ładowany przez external script (w iframe); "normalny tryb" - moduły importowane dynamicznie
                    !exSettings?.isPoczta && (await importModules());

                    const prebidConfig = getPrebidConfig();
                    windowToLoadScript.pbjs.setConfig(prebidConfig);
                    windowToLoadScript.pbjs.bidderSettings = {
                        criteo: {
                            storageAllowed: true,
                        },
                    };

                    windowToLoadScript.pbjs!.setBidderConfig({
                        bidders: ['rtbhouse'],
                        config: {
                            schain: {
                                validation: 'strict',
                                config: {
                                    ver: '1.0',
                                    complete: 1,
                                    nodes: [
                                        {
                                            asi: detection!.host,
                                            sid: 'oScqvFoFcqVeQET3ISu3',
                                            hp: 1,
                                        },
                                    ],
                                },
                            },
                        },
                    });

                    resolve();
                });
            } catch (error) {
                UANC instanceof DebugUANC &&
                    UANC.warn(`[initPrebid] Failure on load Prebid. ${error instanceof Error ? error.message : error}`);
                resolve();
            }
        })();
    });
};
